/**
 * Industries Data Registry
 * Central registry for all Inventive HQ industry-specific pages
 * Based on WordPress backup industry pages: Healthcare, Finance, Legal, MSP, SaaS
 */

export interface Industry {
  name: string;
  slug: string;
  description: string;
  tagline?: string;
  featured?: boolean; // Show on homepage or industries page
  icon?: string; // Path to icon image
  heroImage?: string; // Background image for hero section
  complianceFrameworks?: string[]; // e.g., HIPAA, PCI-DSS, etc.
  keyThreats?: string[]; // Industry-specific threats
  comingSoon?: boolean;
  lastModified?: string; // ISO date string for sitemap lastmod
}

// All Industries
export const industries: Industry[] = [
  {
    name: "Healthcare",
    slug: "healthcare",
    description: "Tackling Healthcare's Biggest Cybersecurity Challenges. From HIPAA compliance to medical device security and EHR protection, our healthcare-specific solutions help you stay ahead of cyber threats while maintaining patient care excellence.",
    tagline: "Protecting Patient Care and Data",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8b78f939-d2e6-472f-7199-c5ef3dfc7600/public",
    heroImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/hero-healthcare/public",
    complianceFrameworks: ["HIPAA", "HITECH", "HITRUST"],
    keyThreats: [
      "Patient data breaches",
      "Ransomware attacks on medical facilities",
      "Medical device vulnerabilities",
      "EHR system security"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "Finance",
    slug: "finance",
    description: "Financial Services Cybersecurity That Keeps You Compliant & Protected. Navigate complex financial regulations, protect sensitive customer data, and build trust with cybersecurity solutions designed specifically for banks, credit unions, and financial institutions.",
    tagline: "Compliance-First Financial Security",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/78ea68e2-724c-46e6-f343-4e51a6512400/public",
    complianceFrameworks: ["PCI-DSS", "SOX", "GLBA", "FFIEC"],
    keyThreats: [
      "Financial fraud and wire transfer attacks",
      "Customer data theft",
      "Account takeover attacks",
      "Regulatory compliance violations"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "Legal",
    slug: "legal",
    description: "Legal Industry Cybersecurity That Keeps You Compliant & Protected. Navigate state bar regulations, protect attorney-client privilege, and build client trust with cybersecurity solutions designed specifically for law firms and legal service providers.",
    tagline: "Protecting Client Confidentiality",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    heroImage: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/hero-legal/public",
    complianceFrameworks: ["State Bar Ethics Rules", "ABA Model Rules", "GDPR"],
    keyThreats: [
      "Attorney-client privilege breaches",
      "Case file theft and espionage",
      "Email compromise and wire fraud",
      "Ethical duty violations"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "MSP",
    slug: "msp",
    description: "Managed Service Provider (MSP) cybersecurity solutions. Protect your clients and your reputation with enterprise-grade security designed for MSPs managing multiple customer environments.",
    tagline: "Secure Your Clients, Protect Your Business",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    complianceFrameworks: ["SOC 2", "ISO 27001"],
    keyThreats: [
      "Supply chain attacks via MSP",
      "Multi-tenant security breaches",
      "Customer data segregation",
      "Privileged access abuse"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "SaaS",
    slug: "saas",
    description: "SaaS cybersecurity solutions for software companies. Build customer trust and meet compliance requirements with security controls designed for cloud-native software platforms.",
    tagline: "Build Trust, Scale Security",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/caac7623-158b-47e2-500f-b49edbae5f00/public",
    complianceFrameworks: ["SOC 2", "ISO 27001", "GDPR", "CCPA"],
    keyThreats: [
      "Multi-tenant data breaches",
      "API vulnerabilities",
      "Account takeover attacks",
      "Third-party integration risks"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "HR & Payroll",
    slug: "hr-payroll",
    description: "HR and Payroll Cybersecurity That Protects Your Workforce. From preventing W-2 phishing scams to securing employee PII and stopping payroll fraud, our HR-specific solutions protect your most sensitive people data while maintaining compliance.",
    tagline: "Protecting Your People and Their Data",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8b78f939-d2e6-472f-7199-c5ef3dfc7600/public",
    complianceFrameworks: ["GDPR", "CCPA", "SOC 2", "SOX"],
    keyThreats: [
      "W-2 phishing and tax fraud schemes",
      "Payroll system breaches and fraud",
      "Employee PII data theft",
      "Insider threats and unauthorized access"
    ],
    lastModified: "2025-01-02"
  },
  {
    name: "Manufacturing",
    slug: "manufacturing",
    description: "Securing the Digital Factory Floor. Manufacturing faces unique cybersecurity challenges with the convergence of IT and OT systems, legacy industrial control systems, and sophisticated threats targeting critical infrastructure. Our manufacturing-specific solutions protect both your business operations and industrial processes while maintaining operational continuity.",
    tagline: "Protect Your Operations, Secure Your Future",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/771207c1-ee9c-4c29-c70d-0cb0c5278400/public",
    complianceFrameworks: ["NIST Cybersecurity Framework", "ISA/IEC 62443", "ISO 27001", "NERC CIP"],
    keyThreats: [
      "Ransomware attacks targeting production systems",
      "IT/OT convergence vulnerabilities",
      "Legacy industrial control system exploits",
      "Supply chain cyberattacks",
      "Credential theft and lateral movement",
      "IIoT device compromise and malware"
    ],
    lastModified: "2025-02-25"
  },
  {
    name: "Education",
    slug: "education",
    description: "Educational Institution Cybersecurity That Protects Students and Data. From K-12 schools to universities, educational institutions face unique challenges including limited budgets, diverse user bases, and sensitive student data protection requirements. Our education-focused solutions provide comprehensive security while enabling learning innovation.",
    tagline: "Secure Learning Environments",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/8b78f939-d2e6-472f-7199-c5ef3dfc7600/public",
    complianceFrameworks: ["FERPA", "COPPA", "CIPA", "GDPR", "State Privacy Laws"],
    keyThreats: [
      "Student data breaches and identity theft",
      "Ransomware attacks on school systems",
      "Phishing targeting faculty and students",
      "Compromised learning management systems",
      "Financial aid fraud and wire transfer scams",
      "Unauthorized access to academic records"
    ],
    lastModified: "2025-02-25"
  },
  {
    name: "Retail & eCommerce",
    slug: "retail-ecommerce",
    description: "Retail and eCommerce Cybersecurity That Protects Your Business and Customers. From payment processing security to customer data protection, retail organizations face constant threats from cybercriminals targeting financial transactions and personal information. Our retail-specific solutions secure your operations while maintaining customer trust.",
    tagline: "Secure Transactions, Trusted Shopping",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/78ea68e2-724c-46e6-f343-4e51a6512400/public",
    complianceFrameworks: ["PCI-DSS", "GDPR", "CCPA", "SOX", "CPRA"],
    keyThreats: [
      "Payment card data theft and skimming",
      "Customer personal information breaches",
      "E-commerce platform vulnerabilities",
      "Point-of-sale system malware",
      "Supply chain attacks on retail systems",
      "Inventory management system compromise"
    ],
    lastModified: "2025-02-25"
  },
  {
    name: "Nonprofits",
    slug: "nonprofits",
    description: "Nonprofit Organization Cybersecurity That Protects Your Mission. Nonprofits are increasingly targeted by cybercriminals seeking donor information, financial data, and operational disruption. Our nonprofit-focused solutions provide enterprise-grade security on nonprofit budgets while protecting your donors, beneficiaries, and mission-critical operations.",
    tagline: "Protect Your Mission, Secure Your Impact",
    featured: true,
    icon: "https://imagedelivery.net/GtU8Bnoa3QuAEdxQ_M9RPA/173d7b37-d0fb-4a15-3e04-919b4d9cf000/public",
    complianceFrameworks: ["SOX (if applicable)", "GDPR", "CCPA", "Grant Compliance Requirements"],
    keyThreats: [
      "Donor information theft and fraud",
      "Grant and financial data breaches",
      "Ransomware targeting operations",
      "Email compromise and wire fraud",
      "Volunteer and beneficiary data exposure",
      "Fraudulent donation processing"
    ],
    lastModified: "2025-02-25"
  }
];

// Helper functions
export function getAllIndustries(): Industry[] {
  return industries;
}

export function getIndustryBySlug(slug: string): Industry | undefined {
  return industries.find(industry => industry.slug === slug);
}

export function getFeaturedIndustries(): Industry[] {
  return industries.filter(industry => industry.featured && !industry.comingSoon);
}

// Get related industries (exclude current)
export function getRelatedIndustries(currentSlug: string, limit: number = 3): Industry[] {
  return industries
    .filter(industry =>
      industry.slug !== currentSlug &&
      !industry.comingSoon
    )
    .slice(0, limit);
}
